import { Trophy, Heart, Users, Music, Swords, Award, TrendingUp, Loader2, LogIn } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { usersApi } from '../lib/api';
import { toast } from 'sonner';
import { useFansData } from '../hooks/useFansData';
import LeaderboardRow from '../components/fans/LeaderboardRow';
import BattleHistoryItem from '../components/fans/BattleHistoryItem';

const ACHIEVEMENTS = (u) => [
  { name: 'Première Battle', emoji: '🎉', desc: 'Ta première battle', unlocked: (u?.battles_total ?? 0) >= 1 },
  { name: '10 Victoires', emoji: '🏆', desc: 'Gagne 10 battles', unlocked: (u?.battles_won ?? 0) >= 10 },
  { name: 'Star Montante', emoji: '⭐', desc: '100 fans atteints', unlocked: (u?.fans ?? 0) >= 100 },
  { name: 'Niveau 5', emoji: '🔥', desc: 'Atteins niveau 5', unlocked: (u?.level ?? 0) >= 5 },
  { name: '500 Likes', emoji: '❤️', desc: 'Reçois 500 likes', unlocked: (u?.total_likes ?? 0) >= 500 },
  { name: 'IA Crusher', emoji: '🤖', desc: "Bats l'IA 25 fois", unlocked: (u?.battles_won ?? 0) >= 25 },
  { name: 'Top 100', emoji: '👑', desc: 'Atteins le top 100', unlocked: (u?.rank ?? 9999) <= 100 },
  { name: 'Niv. 10', emoji: '🚀', desc: 'Atteins niveau 10', unlocked: (u?.level ?? 0) >= 10 },
];

export default function Fans() {
  const { user, requireAuth } = useAuth();
  const { leaderboard, battles, loadingLb, loadingB, bumpFanCount } = useFansData(user);

  const handleFollow = async (targetId) => {
    if (!requireAuth('register')) return;
    try {
      const r = await usersApi.toggleFollow(targetId);
      toast.success(r.following ? 'Tu suis cette star 🔥' : 'Plus suivi(e)');
      bumpFanCount(targetId, r.following ? 1 : -1);
    } catch {
      toast.error('Action impossible');
    }
  };

  const winRate = user && user.battles_total > 0 ? Math.round((user.battles_won / user.battles_total) * 100) : 0;
  const xpPercent = user ? Math.round((user.xp / user.xp_to_next) * 100) : 0;

  return (
    <div className="relative pt-24 pb-12 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {user ? (
          <div className="glass rounded-3xl p-6 md:p-8 mb-8 relative overflow-hidden" data-testid="fans-profile-card">
            <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 via-purple-600/5 to-blue-500/10 pointer-events-none" />
            <div className="relative grid md:grid-cols-3 gap-6 items-center">
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-pink-500 via-purple-600 to-blue-500 flex items-center justify-center text-5xl shadow-xl shadow-pink-500/40">
                  {user.avatar}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-black">{user.username}</h2>
                    <Badge className="bg-yellow-500/20 text-yellow-300 border-0">Niv. {user.level}</Badge>
                  </div>
                  <p className="text-sm text-zinc-400 mt-1">Rang #{user.rank}</p>
                  <div className="mt-2 w-48">
                    <div className="flex justify-between text-xs text-zinc-400 mb-1">
                      <span>XP</span><span>{user.xp}/{user.xp_to_next}</span>
                    </div>
                    <Progress value={xpPercent} className="h-2 bg-white/10" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 md:col-span-2">
                {[
                  { icon: Heart, label: 'Fans', value: user.fans, color: 'text-pink-400' },
                  { icon: Users, label: 'Suivis', value: user.following, color: 'text-purple-400' },
                  { icon: Swords, label: 'Victoires', value: `${user.battles_won} (${winRate}%)`, color: 'text-yellow-400' },
                  { icon: Music, label: 'Chansons', value: user.songs_created, color: 'text-blue-400' },
                ].map((s) => (
                  <div key={s.label} className="glass-light rounded-2xl p-3">
                    <s.icon className={s.color} size={20} />
                    <div className="text-2xl font-black text-white mt-1">{s.value}</div>
                    <div className="text-xs text-zinc-400">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="glass rounded-3xl p-10 mb-8 text-center" data-testid="fans-login-cta">
            <div className="text-5xl mb-3">🎤</div>
            <h2 className="text-2xl font-black mb-2">Connecte-toi pour créer ton profil</h2>
            <p className="text-zinc-400 mb-5">Sauvegarde tes battles, suis tes idoles, monte au classement.</p>
            <Button onClick={() => requireAuth('register')} className="btn-neon text-white border-0 h-11 px-5 rounded-xl font-bold">
              <LogIn size={16} className="mr-1.5" /> Créer mon compte
            </Button>
          </div>
        )}

        <Tabs defaultValue="leaderboard" className="w-full">
          <TabsList className="glass bg-transparent border border-white/10 rounded-2xl p-1 mb-6 inline-flex h-auto">
            <TabsTrigger value="leaderboard" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-purple-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-semibold" data-testid="tab-leaderboard">
              <Trophy size={14} className="mr-1.5" /> Classement
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-purple-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-semibold" data-testid="tab-history">
              <TrendingUp size={14} className="mr-1.5" /> Mes battles
            </TabsTrigger>
            <TabsTrigger value="achievements" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-purple-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-semibold" data-testid="tab-achievements">
              <Award size={14} className="mr-1.5" /> Trophées
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leaderboard">
            {loadingLb ? (
              <div className="flex justify-center py-12"><Loader2 className="animate-spin text-pink-400" size={32} /></div>
            ) : leaderboard.length === 0 ? (
              <div className="glass rounded-3xl p-10 text-center text-zinc-400">Aucun créateur encore. Sois le premier ! 🚀</div>
            ) : (
              <div className="glass rounded-3xl overflow-hidden">
                {leaderboard.map((u, i) => (
                  <LeaderboardRow
                    key={u.id}
                    rank={i}
                    leader={u}
                    canFollow={!!user && user.id !== u.id}
                    onFollow={handleFollow}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="history">
            {!user ? (
              <div className="glass rounded-3xl p-10 text-center text-zinc-400">Connecte-toi pour voir ton historique.</div>
            ) : loadingB ? (
              <div className="flex justify-center py-12"><Loader2 className="animate-spin text-pink-400" size={32} /></div>
            ) : battles.length === 0 ? (
              <div className="glass rounded-3xl p-10 text-center text-zinc-400">
                Aucune battle encore. Lance ta première ! ⚔️
              </div>
            ) : (
              <div className="glass rounded-3xl overflow-hidden">
                {battles.map((b) => (<BattleHistoryItem key={b.id} battle={b} />))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="achievements">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {ACHIEVEMENTS(user).map((a) => (
                <div key={a.name} className={`glass rounded-2xl p-5 text-center card-hover ${!a.unlocked ? 'opacity-40' : ''}`}>
                  <div className="text-5xl mb-2">{a.emoji}</div>
                  <div className="font-bold text-white text-sm">{a.name}</div>
                  <div className="text-xs text-zinc-400 mt-1">{a.desc}</div>
                  {a.unlocked && <Badge className="mt-2 bg-green-500/20 text-green-300 border-0 text-[10px]">Débloqué</Badge>}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
